import {
  contrastRatio,
  isPartyBrandingActive,
  PARTY_BRANDING_TEXT_CONTRAST,
  partyBrandMark,
  resolvePartyBrandingTheme,
  type PartyBranding,
} from "./party-branding";

export const PARTY_OG_WIDTH = 1200;
export const PARTY_OG_HEIGHT = 630;
export const PARTY_OG_LOGO_TIMEOUT_MS = 1800;

const UNBRANDED_QR_DARK = "#0e0c0a";
const UNBRANDED_QR_LIGHT = "#ffffff";

export type PartyShareFacts = {
  partyId: string;
  partyName: string;
  showName: string;
  showYear: number;
  branding: PartyBranding | null;
};

export type PartyOgCard = {
  companyName: string;
  /** Null when a loaded wordmark already shows the name. */
  typedName: string | null;
  partyName: string;
  showLabel: string;
  tagline: string | null;
  primary: string;
  accent: string;
  onPrimary: "#000000" | "#FFFFFF";
  showPoweredBy: boolean;
  logoUrl: string | null;
  logoIsWordmark: boolean;
};

export type PartyLinkCopy = {
  title: string;
  description: string;
  imageAlt: string;
};

export type PartyLinkPreview = {
  title: string;
  description: string;
  imageUrl: string;
  imageAlt: string;
  imageWidth: number;
  imageHeight: number;
};

export type PartyEmailBrand = {
  companyName: string;
  typedName: string | null;
  tagline: string | null;
  logoUrl: string | null;
  primary: string;
  accent: string;
  onPrimary: "#000000" | "#FFFFFF";
  showPoweredBy: boolean;
};

export function partyOgImagePath(partyId: string): string {
  return `/og/parties/${encodeURIComponent(partyId)}`;
}

export function partyShareShowLabel(showName: string, showYear: number): string {
  return `${showName.trim()} ${showYear}`.trim();
}

export function buildPartyOgCard(facts: PartyShareFacts): PartyOgCard | null {
  if (!isPartyBrandingActive(facts.branding)) return null;
  const theme = resolvePartyBrandingTheme(facts.branding);
  const mark = partyBrandMark(facts.branding);
  return {
    companyName: facts.branding.displayName,
    typedName: mark.typedName,
    partyName: facts.partyName.trim(),
    showLabel: partyShareShowLabel(facts.showName, facts.showYear),
    tagline: facts.branding.tagline,
    primary: theme.primary,
    accent: theme.accent,
    onPrimary: theme.onPrimary,
    showPoweredBy: facts.branding.showPoweredBy,
    logoUrl: mark.imageUrl,
    logoIsWordmark: mark.imageIsWordmark,
  };
}

/**
 * A wide wordmark already spells the company. An icon, a square raster
 * (SVG text that did not survive), or a missing image still needs the name.
 */
const PARTY_OG_WORDMARK_ASPECT = 2.2;

export function partyOgTypedName(
  card: PartyOgCard,
  logo: { width: number; height: number } | null,
): string | null {
  if (card.typedName) return card.typedName;
  if (!logo || logo.width <= 0 || logo.height <= 0) return card.companyName;
  if (logo.width / logo.height < PARTY_OG_WORDMARK_ASPECT) return card.companyName;
  return null;
}

export function buildPartyLinkPreview(input: {
  facts: PartyShareFacts;
  origin: string;
  copy: PartyLinkCopy;
}): PartyLinkPreview | null {
  if (!buildPartyOgCard(input.facts)) return null;
  const origin = input.origin.trim().replace(/\/$/, "");
  if (!origin) return null;
  return {
    title: input.copy.title,
    description: input.copy.description,
    imageUrl: `${origin}${partyOgImagePath(input.facts.partyId)}`,
    imageAlt: input.copy.imageAlt,
    imageWidth: PARTY_OG_WIDTH,
    imageHeight: PARTY_OG_HEIGHT,
  };
}

export function absoluteBrandingAssetUrl(
  url: string | null | undefined,
  origin: string,
): string | null {
  if (!url) return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("/")) {
    const base = origin.trim().replace(/\/$/, "");
    if (!base) return null;
    return `${base}${trimmed}`;
  }
  return trimmed;
}

export function buildPartyEmailBrand(
  branding: PartyBranding | null | undefined,
  origin: string,
): PartyEmailBrand | null {
  if (!isPartyBrandingActive(branding)) return null;
  const theme = resolvePartyBrandingTheme(branding);
  const mark = partyBrandMark(branding);
  const logoUrl = absoluteBrandingAssetUrl(mark.imageUrl, origin);
  return {
    companyName: branding.displayName,
    typedName: mark.typedName ?? (logoUrl ? null : branding.displayName),
    tagline: branding.tagline,
    logoUrl,
    primary: theme.primary,
    accent: theme.accent,
    onPrimary: theme.onPrimary,
    showPoweredBy: branding.showPoweredBy,
  };
}

export function partyEmailBrandPlainLines(
  brand: PartyEmailBrand | null | undefined,
  poweredByLabel: string,
): string[] {
  if (!brand) return [];
  const lines = [brand.companyName];
  if (brand.tagline) lines.push(brand.tagline);
  if (brand.showPoweredBy && poweredByLabel.trim()) {
    lines.push(poweredByLabel.trim());
  }
  return lines;
}

export function withCompanyEmailSubject(
  companyName: string | null | undefined,
  subject: string,
): string {
  const company = companyName?.trim() ?? "";
  if (!company) return subject;
  return `${company}: ${subject}`;
}

export function inviteQrPalette(
  branding: PartyBranding | null | undefined,
): { dark: string; light: string } {
  if (!isPartyBrandingActive(branding)) {
    return { dark: UNBRANDED_QR_DARK, light: UNBRANDED_QR_LIGHT };
  }
  const theme = resolvePartyBrandingTheme(branding);
  const dark =
    contrastRatio(theme.primary, UNBRANDED_QR_LIGHT) >= PARTY_BRANDING_TEXT_CONTRAST
      ? theme.primary
      : UNBRANDED_QR_DARK;
  return { dark, light: UNBRANDED_QR_LIGHT };
}
